import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon            from '@mui/icons-material/Logout';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import AutoAwesomeRoundedIcon   from '@mui/icons-material/AutoAwesomeRounded';

import { LogOut } from '../../redux/actions/authActions';
import { ColorModeContext } from '../../App';
import LogoSVG from '../../assets/icons/LogoSVG';

import "./styles.scss";

const TopBar = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const user      = useSelector(state => state.auth.user);
  const { toggleColorMode, mode } = useContext(ColorModeContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleAvatarClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose   = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    dispatch(LogOut(navigate));
  };

  return (
    <Box className="topbar flexCenterSBRow">
      {/* Logo */}
      <Box onClick={() => navigate("/app")} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <LogoSVG height="30px" />
      </Box>

      {/* Right actions */}
      <Box className="flexCenter_Row" gap="6px">

        {/* Dark / light toggle */}
        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            size="small"
            onClick={toggleColorMode}
            sx={{ color: 'text.secondary' }}
          >
            {mode === 'dark'
              ? <LightModeOutlinedIcon fontSize="small" />
              : <DarkModeOutlinedIcon  fontSize="small" />
            }
          </IconButton>
        </Tooltip>

        {/* Profile avatar → opens dropdown */}
        {user && (
          <>
            <Tooltip title="Account">
              <IconButton
                size="small"
                onClick={handleAvatarClick}
                sx={{ p: 0.5 }}
                aria-controls={menuOpen ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <Avatar
                  src={user.picture}
                  alt={user.name}
                  sx={{ width: 30, height: 30, cursor: 'pointer' }}
                />
              </IconButton>
            </Tooltip>

            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 220,
                  overflow: 'visible',
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0, right: 16,
                    width: 10, height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                  },
                },
              }}
            >
              {/* User info header */}
              <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Box className="flexCenter_Row" gap="10px">
                  <Avatar
                    src={user.picture}
                    alt={user.name}
                    sx={{ width: 36, height: 36 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={() => { handleMenuClose(); navigate('/profile'); }}
                sx={{ gap: 0 }}
              >
                <ListItemIcon>
                  <PersonOutlineRoundedIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>

              <MenuItem
                onClick={() => { handleMenuClose(); navigate('/pricing'); }}
                sx={{ gap: 0 }}
              >
                <ListItemIcon>
                  <AutoAwesomeRoundedIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                </ListItemIcon>
                <span>Plans &amp; Billing</span>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={handleLogout}
                sx={{ color: 'error.main', mb: 0.5 }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TopBar;